<template>
  <div data-testid="admin-data-requests">
    <SelectButton v-model="statusFilter" :options="filters" optionLabel="label" optionValue="value" :allowEmpty="false"
      class="mb-4" aria-label="Show requests" data-testid="data-request-filter" />

    <div v-if="loading && !requests.length" class="flex flex-col gap-3" data-testid="data-requests-loading">
      <Skeleton v-for="n in 3" :key="n" height="6rem" />
    </div>

    <Message v-else-if="error && !requests.length" severity="error" :closable="false" data-testid="data-requests-error">
      Could not load the data requests.
    </Message>

    <div v-else-if="!requests.length" class="rounded-2xl border border-dashed border-surface-300 py-16 text-center"
      data-testid="data-requests-empty">
      <i class="pi pi-inbox mb-3 text-3xl text-surface-400" />
      <p class="text-sm font-medium">No data requests yet.</p>
      <p v-if="canSend" class="mt-1 text-sm text-surface-500">Ask a class for their blood group, an address, anything students can fill in.</p>
    </div>

    <div v-else class="flex flex-col gap-3">
      <RouterLink v-for="request in requests" :key="request.id"
        :to="{ name: 'admin-student-data-request', params: { id: request.id } }"
        class="block rounded-xl border border-surface-200 bg-surface-0 p-4 transition-colors hover:border-primary"
        :data-testid="`data-request-${request.id}`">
        <div class="flex flex-wrap items-start justify-between gap-3">
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <Tag :value="statusTag(request).label" :severity="statusTag(request).severity" />
              <Tag v-if="request.blocks_portal && request.status === Status.Open" value="Prompt" severity="secondary" icon="pi pi-lock" />
            </div>
            <h3 class="mt-1.5 truncate text-base font-semibold">{{ request.title }}</h3>
            <p class="mt-0.5 text-xs text-surface-500">
              {{ request.audience.label }} · {{ request.status === Status.Open ? dueText(request.due_on) : `Closed ${formatDay(request.closed_at)}` }}
            </p>
          </div>
          <div class="w-full sm:w-56">
            <div class="mb-1 flex justify-between text-xs">
              <span>{{ progressText(request) }}</span>
              <span class="font-semibold">{{ progressPercent(request.completed_count, request.recipients_count) }}%</span>
            </div>
            <ProgressBar :value="progressPercent(request.completed_count, request.recipients_count)" :showValue="false"
              style="height: 0.5rem" :aria-label="progressText(request)" />
          </div>
        </div>
      </RouterLink>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { storeToRefs } from 'pinia';
import { useUserStore } from '@/store/userStore';
import { can, AppPermissions } from '@/utils/permissions';
import { StudentDataRequestStatus } from '@/types/composition-functions';
import { useStudentDataRequests } from '@/composables/queries/useStudentDataRequests';
import { dueText, formatDay, progressPercent, progressText, statusTag } from '@/utils/studentDataRequests';

/** The school's data requests with their progress (Settings → Custom Fields → Students → Requests). */
const Status = StudentDataRequestStatus;

const filters = [
  { value: Status.Open, label: 'Open' },
  { value: Status.Closed, label: 'Closed' },
  { value: null, label: 'All' },
];
const statusFilter = ref<StudentDataRequestStatus | null>(Status.Open);
const { requests, loading, error } = useStudentDataRequests(statusFilter);

const { currentUser } = storeToRefs(useUserStore());
const canSend = computed(() => can(currentUser.value, AppPermissions.Students.Actions.Update, AppPermissions.Students.Resource));
</script>
